import React, {useMemo, useState} from "react";
import {Link, useNavigate, useOutletContext, useSearchParams} from "react-router-dom";
import {useDebounce} from "react-use";
import {Select} from "antd";
import {SwapOutlined} from "@ant-design/icons";
import SidebarComponent from "../../components/SidebarComponent";
import SearchTalentSidebar from "../talent/SearchTalentSidebar";
import TablePagination from "../../components/TablePagination";
import TalentProvider from "../../providers/TalentProvider";
import TalentTile, {TalentTileSkeleton} from "../talent/TalentTile";
import EmptyFeedback from "../../components/EmptyFeedback";
import ComponentError from "../../components/errors/ComponentError";
import UserDiscipline from "../../models/UserDiscipline";
import CandidateActions from "../talent/CandidateActions";

const SORT_OPTIONS_TALENT = {
    best_match: 'Best Match (high to low)',
    min_bill_rate: 'Bill Rate (low to high)',
    max_bill_rate: 'Bill Rate (high to low)',
    experience: 'Total Experience (high to low)',
    key_skills: 'Key Skills (high to low)',
    industries: 'Industry Experience (high to low)',
    first_name: 'Talent Name (A-Z)',
};

const disciplineCategories = TB.map([...laravel.discipline_categories], cat => {
    let option = TB.pick(cat, ['id', 'name', 'disciplines', 'display_order'])
    let disciplines = option.disciplines;//TB.filter(option.disciplines, ['is_contractable', true]);

    option.disciplines = TB.map(disciplines, discipline => TB.pick(discipline, ['id', 'name', 'display_order']))
    option.value = option.id;

    return option
});


const disciplines = TB.map(disciplineCategories, category => {
    return {
        ...category,
        options: category.disciplines.map(discipline => {
            return {
                ...discipline,
                value: discipline.id
            }
        })
    }
})

const industries = TB.map(laravel.client_industries, (name, value) => {
    return {name, value: Number(value), id: Number(value)}
});


const BrowseTalent = () => {
    const {
        searchData,
        hasSetRequiredFilters,
        error,
        criteriaForMatching,
        isSearchLoading,
        ...outletContext
    } = useOutletContext();

    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();
    const [inputValue, setInputValue] = useState(searchParams.get('search') || '');

    // const [selectedTalent, setSelectedTalent] = useState(null);
    // const addToProjectModal = useRef(null);

    const onChangeSort = (key) => {
        const params = new URLSearchParams(searchParams);
        params.set('sort_by', key);
        params.delete('page');

        return requestAnimationFrame(() => setSearchParams(params, {replace: true}));
    }

    const [, cancel] = useDebounce(
        () => {
            const params = new URLSearchParams(searchParams);
            if (inputValue.length && params.get('search') !== inputValue) {
                params.set('search', inputValue);
            } else if (params.has('search')) {
                params.delete('search');
            }
            if (searchParams.toString() !== params.toString()) {
                return requestAnimationFrame(() => setSearchParams(params, {replace: true}));
            }

        }, 600, [inputValue]
    );

    const defaultBrowseFilters = useMemo(() => {
        let acceptedParams = {
            'disciplines': (ids) => TB.filter(ids, id => TB.isInteger(id)),
            'important_skills': (ids) => TB.filter(ids, id => TB.isInteger(id)),
            'nice_to_have_skills': (ids) => TB.filter(ids, id => TB.isInteger(id)),
            'very_important_skills': (ids) => TB.filter(ids, id => TB.isInteger(id)),
            'important_industries': (ids) => TB.filter(ids, id => TB.isInteger(id)),
            'nice_to_have_industries': (ids) => TB.filter(ids, id => TB.isInteger(id)),
            'very_important_industries': (ids) => TB.filter(ids, id => TB.isInteger(id)),
            'location': (id) => TB.keys(TB.invert(laravel.project_visibility_location_settings)).includes(parseInt(id)),
            'latitude': (lat) => !TB.isNaN(parseFloat(lat)),
            'longitude': (lng) => !TB.isNaN(parseFloat(lng)),
            'experience': (ids) => TB.filter(ids, id => TB.isInteger(id)),
            'hired': value => TB.isBoolean(parseInt(value)),
            'favorited': value => TB.isBoolean(parseInt(value)),
            'invited': value => TB.isBoolean(parseInt(value)),
            'pool_ids': value => TB.isArray(value) && TB.filter(value, id => TB.isInteger(id)),
        };

        let params = TB.storedBrowseFilters('talent');
        if(!params) {
            return {}
        }
        let accepted = {
            favorited: 0,
            invited: 0,
            hired: 0,
            categories: [],
            project_templates: [],
            disciplines: [],
            address: null,
            latitude: null,
            longitude: null,
            experience: {
                junior: false,
                intermediate: false,
                senior: false,
            },
            location: TB.locationGlobalSetting(),
            industries: [],
            skills: [],
            pool_ids: [],
            regions: [],
            brands: []
        };

        TB.map(params, (value, key) => {

            if (accepted.hasOwnProperty(key) || key.includes('skills') || key.includes('industries')) {
                if (key.includes('disciplines')) {
                    let roleIds = JSON.parse(value) || [];

                    roleIds = TB.filter(roleIds, role => !TB.isNaN(parseInt(role)))
                    let roles = TB.filter(TB.flatMap(disciplines, 'options'), dis => roleIds.includes(dis.id));
                    accepted.disciplines = TB.uniqBy([
                        ...(accepted.disciplines || []),
                        ...roles
                    ], 'id');
                }else if (key.includes('skills')) {
                    let skillIds = JSON.parse(value) || [];
                    skillIds = TB.filter(skillIds, skill => !TB.isNaN(parseInt(skill)))
                    const skillsByRole = UserDiscipline.skillsForDisciplines(TB.map(TB.flatMap(disciplines, 'options'), 'id')) || [];
                    let skills = TB.filter(skillsByRole, skill => skillIds.includes(skill.id));
                    let scorePrefix = TB.replace(key, '_skills', '');
                    let importance = 1;
                    switch (scorePrefix) {
                        case 'nice_to_have':
                            importance = 1;
                            break;
                        case 'important':
                            importance = 2;
                            break;
                        case 'very_important':
                            importance = 3;
                            break;
                    }
                    skills = TB.map(skills, skill => ({...skill, importance}))

                    accepted[`skills`] = TB.uniqBy([
                        ...(accepted[`skills`] || []),
                        ...skills
                    ], 'id')

                }else if (key.includes('industries')) {
                    let industriesIds = JSON.parse(value) || [];

                    industriesIds = TB.filter(industriesIds, industry => !TB.isNaN(parseInt(industry)))
                    let industriesSearch = TB.filter(industries, industry => industriesIds.includes(industry.id));
                    let scorePrefix = TB.replace(key, '_industries', '');

                    let importance = 1;
                    switch (scorePrefix) {
                        case 'nice_to_have':
                            importance = 1;
                            break;
                        case 'important':
                            importance = 2;
                            break;
                        case 'very_important':
                            importance = 3;
                            break;
                    }
                    industriesSearch = TB.map(industriesSearch, industry => ({...industry, importance}))
                    accepted[`industries`] = TB.uniqBy([
                        ...(accepted[`industries`] || []),
                        ...industriesSearch
                    ], 'id')
                }

                if (key === 'experience') {
                    try {
                        accepted['experience'] = JSON.parse(value);
                    }catch (e) {
                        console.warn('jsonError', e)
                    }
                }

                if (key === 'hired') {
                    accepted.hired = JSON.parse(value)
                }

                if (key === 'favorited') {
                    accepted.favorited = JSON.parse(value)
                }
                if (key === 'invited') {
                    accepted.invited = JSON.parse(value)
                }
                if (key === 'pool_ids') {
                    accepted.pool_ids = JSON.parse(value)

                    accepted.pool_ids = TB.filter(accepted.pool_ids, pool => !TB.isNaN(parseInt(pool)))
                }

                if (key === 'location' && !TB.isNaN(parseInt(JSON.parse(value)))) {
                    accepted.location = parseInt(JSON.parse(value))
                }
                if (key === 'address') {
                    accepted.address = value
                }
                if (key === 'regions') {
                    accepted.regions = JSON.parse(value)

                    accepted.regions = TB.filter(accepted.regions, regionId => !TB.isNaN(parseInt(regionId)))
                }
                if (key === 'latitude' && !TB.isNaN(parseFloat(JSON.parse(value)))) {
                    accepted.latitude = parseFloat(JSON.parse(value))
                }
                if (key === 'longitude' && !TB.isNaN(parseFloat(JSON.parse(value)))) {
                    accepted.longitude = parseFloat(JSON.parse(value))
                }
            }
        })

        return accepted;
    }, [isSearchLoading, searchParams?.toString(), TB.storedBrowseFilters('talent')]);

    return (
        <React.Fragment>
            <div className="flex flex-col flex-1 talent-scaling">
                <div>
                    <div className="h-[75px] bg-black">
                        <div className="flex h-full">
                            <div className="flex-1 p-4 flex flex-col justify-center">
                                <p className="text-sm text-gray-400">Browse Talent</p>
                                <p className="text-xl text-white font-bold">Search talent across Wripple</p>
                            </div>
                            <div className="flex-1" />
                        </div>
                    </div>

                </div>
                <div className="flex flex-1 items-stretch relative" id="browse-talent-container">

                    <SidebarComponent canClose={true}>
                        <SearchTalentSidebar
                            title="Browse Talent"
                            openOnMount={["SELECT_ROLE"]}
                            enableImportance={true} enableExperience={true}
                            searchEnabled={true}
                            resetOnMount={false}
                            useLocalStorage={"talent"}
                            defaultFilters={defaultBrowseFilters || {}}
                        >
                            <div className="self-stretch flex flex-col items-stretch">
                                <div className="flex flex-row my-3">
                                    <Link to={'/client/browse/teams'} replace={true}
                                        className="btn-link btn-link-primary text-sm no-underline p-0 pb-1 pl-4 m-0">
                                        <SwapOutlined /> Switch To Teams
                                    </Link>
                                </div>

                                <hr className="mb-4 mt-0" />
                            </div>

                        </SearchTalentSidebar>
                    </SidebarComponent>

                    <div className="flex-1 flex flex-col py-12 pl-14 pr-10">
                        <div className="talent-pool-add flex flex-1 flex-grow">
                            <div className="flex flex-grow">
                                {!error ? (
                                    <div className="flex-grow flex flex-col items-stretch">

                                        {hasSetRequiredFilters && (
                                            <React.Fragment>
                                                <input
                                                    type="text"
                                                    className="w-full p-3"
                                                    placeholder="Search By Name"
                                                    value={inputValue}
                                                    disabled={!searchData}
                                                    onChange={({currentTarget}) => setInputValue(currentTarget.value)}
                                                />
                                                <div className="my-5">
                                                    <TablePagination
                                                        isDisabled={isSearchLoading || !searchData}
                                                        currentPage={searchData?.current_page || 0}
                                                        useParams
                                                        pagesCount={searchData?.last_page || 0}
                                                        total={searchData?.total || 0}
                                                        perPage={searchData?.per_page || 0}
                                                        contentClassName="flex-grow flex flex-row items-center justify-between"
                                                    >
                                                        <div className="ml-auto flex flex-row gap-1 items-center">
                                                            <p>Sort:</p>
                                                            <Select
                                                                defaultValue="best_match"
                                                                disabled={!searchData}
                                                                value={SORT_OPTIONS_TALENT[searchParams.get('sort_by') || 'best_match']}
                                                                onChange={(key) => onChangeSort(key)}
                                                                bordered={false}
                                                                options={TB.map(SORT_OPTIONS_TALENT, (label, value) => ({
                                                                    value,
                                                                    label
                                                                }))}
                                                                popupClassName="min-w-[275px]"
                                                                getPopupContainer={() => document.getElementById('browse-talent-container')}
                                                                className="text-primary"
                                                                placement="bottomRight"
                                                            />
                                                        </div>
                                                    </TablePagination>
                                                </div>

                                            </React.Fragment>
                                        )}

                                        {hasSetRequiredFilters ? (
                                            <React.Fragment>
                                                <div className="pt-6 flex-grow">
                                                    <div className="browse-list full-card-list justify-center">

                                                        {isSearchLoading ? (
                                                            <div className="flex flex-wrap gap-8 justify-center">
                                                                {TB.map([...Array(12).keys()]
                                                                    , index => (
                                                                        <div className="max-w-[342px] min-w-[342px]"
                                                                             key={`placeholder-${index}`}>
                                                                            <TalentTileSkeleton>
                                                                                <div
                                                                                    className="potential-talent-actions">
                                                                                    <button disabled={true}
                                                                                            className="btn btn-primary btn-block request-btn">
                                                                                        Invite to Project
                                                                                    </button>
                                                                                </div>
                                                                            </TalentTileSkeleton>
                                                                        </div>
                                                                    ))}
                                                            </div>
                                                        ) : (
                                                            searchData?.users?.length === 0 ? (
                                                                <EmptyFeedback>
                                                                    <div className="flex flex-col items-center">
                                                                        <p className="text-sm font-medium text-center pb-6">
                                                                            No talent found within your search filters
                                                                        </p>

                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-primary"
                                                                            onClick={() => {
                                                                                navigate({
                                                                                    pathname: '.',
                                                                                    search: 'reset=1'
                                                                                }, {replace: true})
                                                                            }}
                                                                        >
                                                                            Reset Search
                                                                        </button>
                                                                    </div>
                                                                </EmptyFeedback>
                                                            ) : (
                                                                <div className="flex flex-wrap gap-8 justify-center">
                                                                    {searchData?.users.map(user => (
                                                                        <div key={`tal-${user.id}`}
                                                                             className="max-w-[342px] min-w-[342px]">
                                                                            <TalentProvider
                                                                                talent={user}
                                                                                onUpdated={updatedTalent => {
                                                                                    console.log('onUpdated', updatedTalent)
                                                                                }}
                                                                                // onUpdated={this.refreshTalentFor.bind(this)}
                                                                                criteria={criteriaForMatching}
                                                                                key={`tal-${user.id}`}>
                                                                                <TalentTile
                                                                                    onUpdated={updatedTalent => {
                                                                                        console.log('onUpdated', updatedTalent)
                                                                                    }}
                                                                                    placeholder={isSearchLoading}>

                                                                                    <div className="potential-talent-actions">
                                                                                        <CandidateActions hideRates={true} isDisabled={isSearchLoading}
                                                                                                          className="btn-block request-btn"/>
                                                                                    </div>
                                                                                </TalentTile>
                                                                            </TalentProvider>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="pt-8">
                                                    {searchData?.last_page > 1 && (
                                                        <TablePagination
                                                            isDisabled={isSearchLoading}
                                                            currentPage={searchData.current_page}
                                                            useParams
                                                            pagesCount={searchData.last_page}
                                                            total={searchData.total}
                                                            perPage={searchData.per_page}
                                                        />
                                                    )}
                                                </div>
                                            </React.Fragment>
                                        ) : (
                                            <EmptyFeedback>
                                                <div className="flex flex-col items-center">
                                                    <p className="text-sm font-medium text-center pb-6">
                                                        Select at least one role to search talent
                                                    </p>

                                                    {/*{searchParams?.toString()?.length > 0 && (*/}
                                                    {/*    <button*/}
                                                    {/*        type="button"*/}
                                                    {/*        className="btn btn-primary mt-2"*/}
                                                    {/*        onClick={() => {*/}
                                                    {/*            navigate({pathname: '.', search: 'reset=1'}, {replace: true})*/}
                                                    {/*        }}*/}
                                                    {/*    >*/}
                                                    {/*        Reset Search*/}
                                                    {/*    </button>*/}
                                                    {/*)}*/}
                                                </div>
                                            </EmptyFeedback>
                                        )}
                                    </div>
                                ) : (
                                    <ComponentError>
                                        {searchParams?.toString()?.length > 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-primary mt-2"
                                                onClick={() => {
                                                    navigate({pathname: '.', search: 'reset=1'}, {replace: true})
                                                }}
                                            >
                                                Reset Search
                                            </button>
                                        )}
                                    </ComponentError>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )
}

export default BrowseTalent;